import { useState, useEffect, useCallback } from 'react';
import { Settings as SettingsIcon, DollarSign, RefreshCw, Save, TrendingUp, Database, Info, Percent, Users, Pencil, Trash2, X, Calendar, Download, Hotel, Mail, Eye, Clock, Bell } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Settings state
  const [rptCost, setRptCost] = useState('');
  const [dataCost, setDataCost] = useState('');
  const [usdRate, setUsdRate] = useState('');
  const [eurRate, setEurRate] = useState('');
  const [gbpRate, setGbpRate] = useState('');
  const [elsaFrostCommission, setElsaFrostCommission] = useState('');
  
  // User Commission Rates
  const [users, setUsers] = useState([]);
  const [userCommissionRates, setUserCommissionRates] = useState([]);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [editSalesRate, setEditSalesRate] = useState('4');
  const [editReferenceRate, setEditReferenceRate] = useState('5');
  const [editElsaFrostRate, setEditElsaFrostRate] = useState('4');
  const [savingCommission, setSavingCommission] = useState(false);

  // Daily Exchange Rates
  const [dailyRates, setDailyRates] = useState([]);
  const [showDailyRateDialog, setShowDailyRateDialog] = useState(false);
  const [dailyRateDate, setDailyRateDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyUsdRate, setDailyUsdRate] = useState('');
  const [dailyEurRate, setDailyEurRate] = useState('');
  const [dailyGbpRate, setDailyGbpRate] = useState('');
  const [fetchingRates, setFetchingRates] = useState(false);
  const [fetchRangeStart, setFetchRangeStart] = useState('');
  const [fetchRangeEnd, setFetchRangeEnd] = useState('');

  // Hotel Reservation Form URL
  const [hotelFormUrl, setHotelFormUrl] = useState('');

  // Email Template Settings
  const [emailTemplate, setEmailTemplate] = useState({
    subject: '2. Visit Hatırlatma - {{patient_count}} Hasta Bekliyor',
    heading: '2. Visit Hatırlatma',
    intro_text: 'Merhaba, aşağıdaki hastaların 2. visit randevusu 3 ayı geçmiştir. Lütfen hastaları arayarak randevu oluşturunuz.',
    footer_text: 'Bu e-posta {{from_name}} tarafından otomatik olarak gönderilmiştir.',
    from_name: 'Dentordu'
  });
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [savingEmailTemplate, setSavingEmailTemplate] = useState(false);

  // Auto Reminder Settings
  const [autoReminderEnabled, setAutoReminderEnabled] = useState(false);
  const [savingAutoReminder, setSavingAutoReminder] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, usersRes, ratesRes, dailyRatesRes, emailTemplateRes, autoReminderRes] = await Promise.all([
        axios.get(`${API_URL}/api/settings/all`, getAuthHeader()),
        axios.get(`${API_URL}/api/users`, getAuthHeader()),
        axios.get(`${API_URL}/api/settings/user-commission-rates`, getAuthHeader()),
        axios.get(`${API_URL}/api/exchange-rates/history`, getAuthHeader()),
        axios.get(`${API_URL}/api/settings/email-template`, getAuthHeader()).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/settings/auto-reminder`, getAuthHeader()).catch(() => ({ data: { enabled: false } }))
      ]);
      
      setRptCost(settingsRes.data.cost_per_rpt?.toString() || '0');
      setDataCost(settingsRes.data.cost_per_data?.toString() || '0');
      setUsdRate(settingsRes.data.usd_rate?.toString() || '35');
      setEurRate(settingsRes.data.eur_rate?.toString() || '38');
      setGbpRate(settingsRes.data.gbp_rate?.toString() || '45');
      setElsaFrostCommission(settingsRes.data.elsa_frost_commission_rate?.toString() || '4');
      setHotelFormUrl(settingsRes.data.hotel_form_url || '');
      
      // Load email template if exists
      if (emailTemplateRes.data) {
        setEmailTemplate(prev => ({
          ...prev,
          ...emailTemplateRes.data
        }));
      }
      
      // Load auto reminder settings
      setAutoReminderEnabled(autoReminderRes.data?.enabled || false);
      
      setUsers(usersRes.data);
      setUserCommissionRates(ratesRes.data);
      setDailyRates(dailyRatesRes.data || []);
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveRptCost = async () => {
    setSaving(s => ({ ...s, rpt: true }));
    try {
      await axios.post(`${API_URL}/api/settings/rpt-cost`, {
        cost_per_rpt: parseFloat(rptCost) || 0
      }, getAuthHeader());
      toast.success('RPT maliyeti güncellendi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(s => ({ ...s, rpt: false }));
    }
  };

  const handleSaveDataCost = async () => {
    setSaving(s => ({ ...s, data: true }));
    try {
      await axios.post(`${API_URL}/api/settings/data-cost`, {
        cost_per_data: parseFloat(dataCost) || 0
      }, getAuthHeader());
      toast.success('Data maliyeti güncellendi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(s => ({ ...s, data: false }));
    }
  };

  const handleSaveElsaFrostCommission = async () => {
    setSaving(s => ({ ...s, elsaFrost: true }));
    try {
      await axios.post(`${API_URL}/api/settings/elsa-frost-commission`, {
        commission_rate: parseFloat(elsaFrostCommission) || 4
      }, getAuthHeader());
      toast.success('ELSA FROST komisyon oranı güncellendi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(s => ({ ...s, elsaFrost: false }));
    }
  };

  const handleSaveHotelFormUrl = async () => {
    setSaving(s => ({ ...s, hotel: true }));
    try {
      await axios.post(`${API_URL}/api/settings/hotel-form-url`, {
        form_url: hotelFormUrl
      }, getAuthHeader());
      toast.success('Otel rezervasyon form URL güncellendi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(s => ({ ...s, hotel: false }));
    }
  };

  // User Commission Functions
  const handleOpenCommissionDialog = (user) => {
    const existingRate = userCommissionRates.find(r => r.user_email === user.email.toLowerCase());
    setSelectedUserEmail(user.email);
    setEditSalesRate(existingRate?.sales_rate?.toString() || '4');
    setEditReferenceRate(existingRate?.reference_rate?.toString() || '5');
    setEditElsaFrostRate(existingRate?.elsa_frost_rate?.toString() || elsaFrostCommission || '4');
    setShowCommissionDialog(true);
  };

  const handleSaveUserCommission = async () => {
    setSavingCommission(true);
    try {
      await axios.post(`${API_URL}/api/settings/user-commission-rates`, {
        user_email: selectedUserEmail,
        sales_rate: parseFloat(editSalesRate) || 4,
        reference_rate: parseFloat(editReferenceRate) || 5,
        elsa_frost_rate: parseFloat(editElsaFrostRate) || 4
      }, getAuthHeader());
      
      toast.success('Komisyon oranları kaydedildi');
      setShowCommissionDialog(false);
      
      // Refresh rates
      const ratesRes = await axios.get(`${API_URL}/api/settings/user-commission-rates`, getAuthHeader());
      setUserCommissionRates(ratesRes.data);
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSavingCommission(false);
    }
  };

  const handleDeleteUserCommission = async (userEmail) => {
    if (!window.confirm('Bu kullanıcının özel komisyon oranını silmek istediğinize emin misiniz? Varsayılan oranlar kullanılacak.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/settings/user-commission-rates/${encodeURIComponent(userEmail)}`, getAuthHeader());
      toast.success('Komisyon oranı silindi, varsayılan değerlere döndü');
      
      // Refresh rates
      const ratesRes = await axios.get(`${API_URL}/api/settings/user-commission-rates`, getAuthHeader());
      setUserCommissionRates(ratesRes.data);
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // Daily Exchange Rate Functions
  const handleSaveDailyRate = async () => {
    if (!dailyRateDate || !dailyUsdRate || !dailyEurRate || !dailyGbpRate) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    
    setFetchingRates(true);
    try {
      await axios.post(
        `${API_URL}/api/exchange-rates/set-manual?date=${dailyRateDate}&usd_rate=${dailyUsdRate}&eur_rate=${dailyEurRate}&gbp_rate=${dailyGbpRate}`,
        {},
        getAuthHeader()
      );
      toast.success(`${dailyRateDate} için kurlar kaydedildi`);
      setShowDailyRateDialog(false);
      
      // Refresh daily rates
      const dailyRatesRes = await axios.get(`${API_URL}/api/exchange-rates/history`, getAuthHeader());
      setDailyRates(dailyRatesRes.data || []);
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setFetchingRates(false);
    }
  };

  const handleFetchRateRange = async () => {
    if (!fetchRangeStart || !fetchRangeEnd) {
      toast.error('Başlangıç ve bitiş tarihlerini seçin');
      return;
    }
    
    setFetchingRates(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/exchange-rates/fetch-range?start_date=${fetchRangeStart}&end_date=${fetchRangeEnd}`,
        {},
        getAuthHeader()
      );
      toast.success(response.data.message);
      
      // Refresh daily rates
      const dailyRatesRes = await axios.get(`${API_URL}/api/exchange-rates/history`, getAuthHeader());
      setDailyRates(dailyRatesRes.data || []);
    } catch (error) {
      toast.error('Kurlar çekilemedi');
    } finally {
      setFetchingRates(false);
    }
  };

  const openEditDailyRate = (rate) => {
    setDailyRateDate(rate.date);
    setDailyUsdRate(rate.usd_rate?.toString() || '');
    setDailyEurRate(rate.eur_rate?.toString() || '');
    setDailyGbpRate(rate.gbp_rate?.toString() || '');
    setShowDailyRateDialog(true);
  };

  const openNewDailyRate = () => {
    setDailyRateDate(new Date().toISOString().split('T')[0]);
    setDailyUsdRate(usdRate);
    setDailyEurRate(eurRate);
    setDailyGbpRate(gbpRate);
    setShowDailyRateDialog(true);
  };

  const getUserCommissionRate = (email) => {
    return userCommissionRates.find(r => r.user_email === email.toLowerCase());
  };

  const handleSaveExchangeRates = async () => {
    setSaving(s => ({ ...s, exchange: true }));
    try {
      await axios.post(`${API_URL}/api/settings/exchange-rates`, {
        usd_rate: parseFloat(usdRate) || 35,
        eur_rate: parseFloat(eurRate) || 38,
        gbp_rate: parseFloat(gbpRate) || 45
      }, getAuthHeader());
      toast.success('Döviz kurları güncellendi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(s => ({ ...s, exchange: false }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Email Template Functions
  const handleSaveEmailTemplate = async () => {
    setSavingEmailTemplate(true);
    try {
      await axios.post(`${API_URL}/api/settings/email-template`, emailTemplate, getAuthHeader());
      toast.success('E-posta şablonu kaydedildi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSavingEmailTemplate(false);
    }
  };

  // Auto Reminder Toggle
  const handleToggleAutoReminder = async () => {
    setSavingAutoReminder(true);
    const newValue = !autoReminderEnabled;
    try {
      await axios.post(`${API_URL}/api/settings/auto-reminder`, { enabled: newValue }, getAuthHeader());
      setAutoReminderEnabled(newValue);
      toast.success(newValue ? 'Otomatik hatırlatma aktif edildi' : 'Otomatik hatırlatma kapatıldı');
    } catch (error) {
      toast.error('Ayar güncellenemedi');
    } finally {
      setSavingAutoReminder(false);
    }
  };

  const getEmailPreviewHtml = () => {
    const patientRows = `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;">Örnek Hasta 1</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">İmplant</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">2026-06-01</td>
      </tr>
      <tr>
        <td style="padding:8px;border-bottom:1px solid #eee;">Örnek Hasta 2</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">Diş Beyazlatma</td>
        <td style="padding:8px;border-bottom:1px solid #eee;">2026-05-15</td>
      </tr>
    `;
    
    return `
      <table role="presentation" width="100%" style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;">
        <tr>
          <td style="padding:24px;">
            <h2 style="color:#0891b2;margin:0 0 16px 0;">${emailTemplate.heading}</h2>
            <p style="color:#374151;margin:0 0 16px 0;">${emailTemplate.intro_text}</p>
            <table style="width:100%;border-collapse:collapse;margin:16px 0;">
              <thead>
                <tr style="background-color:#f3f4f6;">
                  <th style="padding:12px 8px;text-align:left;border-bottom:2px solid #e5e7eb;">Hasta Adı</th>
                  <th style="padding:12px 8px;text-align:left;border-bottom:2px solid #e5e7eb;">Tedavi</th>
                  <th style="padding:12px 8px;text-align:left;border-bottom:2px solid #e5e7eb;">İlk Tarih</th>
                </tr>
              </thead>
              <tbody>
                ${patientRows}
              </tbody>
            </table>
            <p style="color:#6b7280;font-size:14px;margin:24px 0 0 0;">
              ${emailTemplate.footer_text.replace('{{from_name}}', emailTemplate.from_name)}
            </p>
          </td>
        </tr>
      </table>
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="settings-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Ayarlar</h2>
          <p className="text-muted-foreground">Sistem yapılandırması ve maliyet ayarları</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-settings">
          <RefreshCw className="h-4 w-4 mr-2" />
          Yenile
        </Button>
      </div>

      {/* Cost Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* RPT Cost Setting */}
        <Card data-testid="rpt-cost-card">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-red-500" />
              RPT Maliyet Ayarı
            </CardTitle>
            <CardDescription>
              RPT satış türü için sabit maliyet tutarını belirleyin. Bu tutar performans hesaplamalarında gider olarak kullanılır.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="rpt_cost">RPT Başı Maliyet (₺)</Label>
                <Input
                  id="rpt_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={rptCost}
                  onChange={(e) => setRptCost(e.target.value)}
                  placeholder="Örn: 100"
                  data-testid="rpt-cost-input"
                />
                <p className="text-sm text-muted-foreground">
                  Şu anki değer: {formatCurrency(parseFloat(rptCost) || 0)}
                </p>
              </div>
              <Button onClick={handleSaveRptCost} disabled={saving.rpt} data-testid="save-rpt-cost-btn">
                {saving.rpt ? (
                  <>
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Cost Setting */}
        <Card data-testid="data-cost-card">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Database className="h-5 w-5 text-blue-500" />
              Data Maliyet Ayarı
            </CardTitle>
            <CardDescription>
              Lead/data başı maliyet tutarını belirleyin. Bu tutar performans hesaplamalarında gider olarak kullanılır.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="data_cost">Data Başı Maliyet (₺)</Label>
                <Input
                  id="data_cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={dataCost}
                  onChange={(e) => setDataCost(e.target.value)}
                  placeholder="Örn: 50"
                  data-testid="data-cost-input"
                />
                <p className="text-sm text-muted-foreground">
                  Şu anki değer: {formatCurrency(parseFloat(dataCost) || 0)}
                </p>
              </div>
              <Button onClick={handleSaveDataCost} disabled={saving.data} data-testid="save-data-cost-btn">
                {saving.data ? (
                  <>
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ELSA FROST Commission Setting */}
        <Card data-testid="elsa-frost-commission-card">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Percent className="h-5 w-5 text-emerald-500" />
              ELSA FROST Komisyon Oranı
            </CardTitle>
            <CardDescription>
              ELSA FROST NETWORK ve ELSA FROST NETWORK DATA satış tipleri için komisyon yüzdesini belirleyin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="elsa_frost_commission">Komisyon Oranı (%)</Label>
                <Input
                  id="elsa_frost_commission"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={elsaFrostCommission}
                  onChange={(e) => setElsaFrostCommission(e.target.value)}
                  placeholder="Örn: 4"
                  data-testid="elsa-frost-commission-input"
                />
                <p className="text-sm text-muted-foreground">
                  Şu anki oran: %{parseFloat(elsaFrostCommission) || 4}
                </p>
              </div>
              <Button onClick={handleSaveElsaFrostCommission} disabled={saving.elsaFrost} data-testid="save-elsa-frost-btn">
                {saving.elsaFrost ? (
                  <>
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Hotel Reservation Form URL */}
        <Card data-testid="hotel-form-card">
          <CardHeader>
            <CardTitle className="font-[Manrope] flex items-center gap-2">
              <Hotel className="h-5 w-5 text-purple-500" />
              Otel Rezervasyon Formu
            </CardTitle>
            <CardDescription>
              Personellerin kullanacağı Google Sheets/Forms rezervasyon formunun URL'ini girin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label htmlFor="hotel_form_url">Form URL</Label>
                <Input
                  id="hotel_form_url"
                  type="url"
                  value={hotelFormUrl}
                  onChange={(e) => setHotelFormUrl(e.target.value)}
                  placeholder="https://docs.google.com/forms/..."
                  data-testid="hotel-form-url-input"
                />
                <p className="text-sm text-muted-foreground">
                  Google Forms veya Google Sheets URL'i yapıştırın
                </p>
              </div>
              <Button onClick={handleSaveHotelFormUrl} disabled={saving.hotel} data-testid="save-hotel-form-btn">
                {saving.hotel ? (
                  <>
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exchange Rates Setting */}
      <Card data-testid="exchange-rates-card">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Döviz Kuru Ayarları
          </CardTitle>
          <CardDescription>
            Varsayılan döviz kurları. Günlük kurlar yoksa bu değerler kullanılır.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="usd_rate">USD Kuru (₺)</Label>
              <Input
                id="usd_rate"
                type="number"
                min="0"
                step="0.01"
                value={usdRate}
                onChange={(e) => setUsdRate(e.target.value)}
                placeholder="Örn: 35"
                data-testid="usd-rate-input"
              />
              <p className="text-sm text-muted-foreground">
                1 USD = {formatCurrency(parseFloat(usdRate) || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="eur_rate">EUR Kuru (₺)</Label>
              <Input
                id="eur_rate"
                type="number"
                min="0"
                step="0.01"
                value={eurRate}
                onChange={(e) => setEurRate(e.target.value)}
                placeholder="Örn: 38"
                data-testid="eur-rate-input"
              />
              <p className="text-sm text-muted-foreground">
                1 EUR = {formatCurrency(parseFloat(eurRate) || 0)}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gbp_rate">GBP Kuru (₺)</Label>
              <Input
                id="gbp_rate"
                type="number"
                min="0"
                step="0.01"
                value={gbpRate}
                onChange={(e) => setGbpRate(e.target.value)}
                placeholder="Örn: 45"
                data-testid="gbp-rate-input"
              />
              <p className="text-sm text-muted-foreground">
                1 GBP = {formatCurrency(parseFloat(gbpRate) || 0)}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={handleSaveExchangeRates} disabled={saving.exchange} data-testid="save-exchange-btn">
              {saving.exchange ? (
                <>
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Varsayılan Kurları Kaydet
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Exchange Rates History */}
      <Card data-testid="monthly-exchange-rates-card">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            Aylık Ortalama Döviz Kurları
          </CardTitle>
          <CardDescription>
            Her satış kaydı, o aya ait ortalama döviz kuruna göre TL'ye çevrilir.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Rates Table */}
          {dailyRates.length > 0 ? (
            <div className="rounded-lg border overflow-hidden max-h-[400px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ay</TableHead>
                    <TableHead className="text-right">USD (₺)</TableHead>
                    <TableHead className="text-right">EUR (₺)</TableHead>
                    <TableHead className="text-right">GBP (₺)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dailyRates.map((rate) => (
                    <TableRow key={rate.month}>
                      <TableCell className="font-medium">
                        {rate.month}
                      </TableCell>
                      <TableCell className="text-right">{rate.usd_rate?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{rate.eur_rate?.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{rate.gbp_rate?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground border rounded-lg">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Henüz aylık döviz kuru kaydı yok.</p>
            </div>
          )}

          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>Bilgi:</strong> Sistem, her satış kaydının tarihine göre o ayın ortalama kurunu kullanır. Toplam 36 aylık (2024-2026) ortalama kurlar kayıtlıdır.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-700 dark:text-blue-400">Performans ve Komisyon Hesaplama Bilgisi</p>
              <div className="text-sm text-blue-600 dark:text-blue-500 mt-2 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium">Satış Bilgisi:</p>
                    <p>SALES + SALES 2ND VISIT → %4 Komisyon</p>
                  </div>
                  <div>
                    <p className="font-medium">Referans Satış Bilgisi:</p>
                    <p>REFERENCE + REFERENCE 2ND VISIT → %5 Komisyon</p>
                  </div>
                </div>
                <hr className="border-blue-200 dark:border-blue-800" />
                <p><strong>Gider Hesaplama:</strong> (RPT Adedi × RPT Maliyeti) + (Lead Adedi × Data Maliyeti)</p>
                <p><strong>Net Kâr:</strong> Toplam Gelir (TL) - Toplam Gider</p>
                <p><strong>Döviz Çevirimi:</strong> USD ve EUR tutarlar belirlenen kurlarla TL ye çevrilir</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Column Mapping Info */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope]">Google Sheets Sütun Eşleştirmesi</CardTitle>
          <CardDescription>
            Sistem Google Sheets verilerini aşağıdaki sütun yapısına göre analiz eder
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun A - Bilet Tarihi</p>
              <p className="text-sm text-muted-foreground">Biletin satın alındığı tarih (Performans için)</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun B - Hasta Adı</p>
              <p className="text-sm text-muted-foreground">Hastanın ismi (Prim sayfasında görünür)</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun E - Ödeme Tutarı</p>
              <p className="text-sm text-muted-foreground">Hasta ödemesi + döviz türü algılama (USD, EUR, GBP, TL)</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun F - Tedavi Tarihi</p>
              <p className="text-sm text-muted-foreground">Komisyon hesaplaması için kullanılır</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun M - Personel E-postası</p>
              <p className="text-sm text-muted-foreground">Sistem kullanıcısıyla eşleştirilir</p>
            </div>
            <div className="p-4 border rounded-lg">
              <p className="font-medium mb-2">Sütun P - Satış Türü</p>
              <p className="text-sm text-muted-foreground">
                Değerler: SALES, SALES 2ND VISIT, REFERENCE, REFERENCE 2ND VISIT, RPT, ELSA FROST
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Commission Rates */}
      <Card data-testid="user-commission-rates-card">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Users className="h-5 w-5 text-orange-500" />
            Personel Bazlı Komisyon Oranları
          </CardTitle>
          <CardDescription>
            Her personel için özel komisyon oranları belirleyebilirsiniz. Ayarlanmamış personeller varsayılan oranları kullanır.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Personel</TableHead>
                  <TableHead className="text-center">Satış (%)</TableHead>
                  <TableHead className="text-center">Referans (%)</TableHead>
                  <TableHead className="text-center">ELSA FROST (%)</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.filter(u => u.role === 'staff').map((user) => {
                  const rate = getUserCommissionRate(user.email);
                  return (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        <div>
                          <p>{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {rate ? (
                          <Badge variant="default" className="bg-blue-500">%{rate.sales_rate}</Badge>
                        ) : (
                          <Badge variant="outline">%4 (varsayılan)</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {rate ? (
                          <Badge variant="default" className="bg-purple-500">%{rate.reference_rate}</Badge>
                        ) : (
                          <Badge variant="outline">%5 (varsayılan)</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {rate ? (
                          <Badge variant="default" className="bg-emerald-500">%{rate.elsa_frost_rate}</Badge>
                        ) : (
                          <Badge variant="outline">%{elsaFrostCommission || 4} (varsayılan)</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenCommissionDialog(user)}
                            data-testid={`edit-commission-${user.email}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {rate && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteUserCommission(user.email)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-commission-${user.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {users.filter(u => u.role === 'staff').length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Henüz personel bulunmuyor
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Not:</strong> Varsayılan oranlar - Satış: %4, Referans: %5, ELSA FROST: %{elsaFrostCommission || 4}.
              Özel oran belirlemek için personel satırındaki düzenle butonunu tıklayın.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Template Settings */}
      <Card data-testid="email-template-card">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Mail className="h-5 w-5 text-cyan-500" />
            E-posta Şablonu Ayarları
          </CardTitle>
          <CardDescription>
            2. Visit hatırlatma e-postası için şablonu özelleştirin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email_subject">E-posta Konusu</Label>
              <Input
                id="email_subject"
                value={emailTemplate.subject}
                onChange={(e) => setEmailTemplate(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="2. Visit Hatırlatma - {{patient_count}} Hasta Bekliyor"
                data-testid="email-subject-input"
              />
              <p className="text-xs text-muted-foreground">
                Kullanılabilir değişkenler: {'{{patient_count}}'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email_from_name">Gönderen Adı</Label>
              <Input
                id="email_from_name"
                value={emailTemplate.from_name}
                onChange={(e) => setEmailTemplate(prev => ({ ...prev, from_name: e.target.value }))}
                placeholder="Dentordu"
                data-testid="email-from-name-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email_heading">Başlık</Label>
            <Input
              id="email_heading"
              value={emailTemplate.heading}
              onChange={(e) => setEmailTemplate(prev => ({ ...prev, heading: e.target.value }))}
              placeholder="2. Visit Hatırlatma"
              data-testid="email-heading-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email_intro">Giriş Metni</Label>
            <Textarea
              id="email_intro"
              value={emailTemplate.intro_text}
              onChange={(e) => setEmailTemplate(prev => ({ ...prev, intro_text: e.target.value }))}
              placeholder="Merhaba, aşağıdaki hastaların 2. visit randevusu 3 ayı geçmiştir..."
              rows={3}
              data-testid="email-intro-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email_footer">Alt Bilgi</Label>
            <Textarea
              id="email_footer"
              value={emailTemplate.footer_text}
              onChange={(e) => setEmailTemplate(prev => ({ ...prev, footer_text: e.target.value }))}
              placeholder="Bu e-posta {{from_name}} tarafından otomatik olarak gönderilmiştir."
              rows={2}
              data-testid="email-footer-input"
            />
            <p className="text-xs text-muted-foreground">
              Kullanılabilir değişkenler: {'{{from_name}}'}
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowEmailPreview(true)}
              className="gap-2"
            >
              <Eye className="h-4 w-4" />
              Önizleme
            </Button>
            <Button
              onClick={handleSaveEmailTemplate}
              disabled={savingEmailTemplate}
              className="gap-2"
              data-testid="save-email-template-btn"
            >
              {savingEmailTemplate ? (
                <>
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Şablonu Kaydet
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Auto Reminder Settings */}
      <Card data-testid="auto-reminder-card">
        <CardHeader>
          <CardTitle className="font-[Manrope] flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-500" />
            Otomatik Hatırlatma
          </CardTitle>
          <CardDescription>
            Her Pazartesi 09:00'da 3+ ay gecikmeli 2. visit hastaları için otomatik e-posta hatırlatması
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Haftalık Hatırlatma</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Her Pazartesi sabah 09:00 (Türkiye Saati)
              </p>
            </div>
            <Button
              variant={autoReminderEnabled ? "default" : "outline"}
              onClick={handleToggleAutoReminder}
              disabled={savingAutoReminder}
              className="gap-2 min-w-[120px]"
              data-testid="auto-reminder-toggle"
            >
              {savingAutoReminder ? (
                <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : autoReminderEnabled ? (
                <>
                  <Bell className="h-4 w-4" />
                  Aktif
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4" />
                  Kapalı
                </>
              )}
            </Button>
          </div>
          {autoReminderEnabled && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Otomatik hatırlatma aktif. 3+ ay gecikmeli hastalar için her Pazartesi e-posta gönderilecek.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Preview Dialog */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">E-posta Önizleme</DialogTitle>
            <DialogDescription>
              Konu: {emailTemplate.subject.replace('{{patient_count}}', '2')}
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg p-4 bg-white">
            <div dangerouslySetInnerHTML={{ __html: getEmailPreviewHtml() }} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailPreview(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Commission Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Komisyon Oranlarını Düzenle</DialogTitle>
            <DialogDescription>
              {selectedUserEmail} için özel komisyon oranları belirleyin
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_sales_rate">Satış Komisyonu (%)</Label>
              <Input
                id="edit_sales_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={editSalesRate}
                onChange={(e) => setEditSalesRate(e.target.value)}
                placeholder="4"
                data-testid="edit-sales-rate-input"
              />
              <p className="text-xs text-muted-foreground">SATIŞ ve SATIŞ 2. VİSİT satışları için</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_reference_rate">Referans Komisyonu (%)</Label>
              <Input
                id="edit_reference_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={editReferenceRate}
                onChange={(e) => setEditReferenceRate(e.target.value)}
                placeholder="5"
                data-testid="edit-reference-rate-input"
              />
              <p className="text-xs text-muted-foreground">REFERANS ve REFERANS 2. VİSİT satışları için</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_elsa_frost_rate">ELSA FROST Komisyonu (%)</Label>
              <Input
                id="edit_elsa_frost_rate"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={editElsaFrostRate}
                onChange={(e) => setEditElsaFrostRate(e.target.value)}
                placeholder="4"
                data-testid="edit-elsa-frost-rate-input"
              />
              <p className="text-xs text-muted-foreground">ELSA FROST NETWORK satışları için</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>
              İptal
            </Button>
            <Button onClick={handleSaveUserCommission} disabled={savingCommission}>
              {savingCommission ? (
                <>
                  <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
